import { QueryTable } from "@/components/query-table/QueryTable";
import PageHeader from "@/components/PageHeader";
import { PATH_PROJECT_ROLE_EDIT } from "@/constants";
import { FetchListRequest } from "@/services/api";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ProjectRoleContextProvider } from "./ProjectRoleContext";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useMemo } from "react";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { ReadProjectRole } from "@/types/projectRoleType";
import { ProjectRolesService } from "@/modules/projects/service/projectRole";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { ProjectRoleListContent } from "@/modules/projects/components/roles/ProjectRoleListContent";
import { readProjectRole } from "@/schemas/projectRole";
import { ProjectRoleAddButton } from "@/modules/projects/components/roles/ProjectRoleAddButton";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { MenubarContainer } from "@/components/MenubarContainer";

const ProjectRoleList = () => {
  const name = "projectRoles";
  const { t } = useTranslation();
  const projectId = useProjectId();
  const projectQuery = useProjectQuery(projectId);
  const options = useCreateDataTableOptions<ReadProjectRole>(name);
  const title = t("projects.breadcrumb.roles");
  const breadcrumbItems = useProjectBreadcrumb(projectQuery, [
    {
      label: title,
    },
  ]);
  const queryKey = useDynamicQueryParams(name, options);

  const fetchList = (fetchParams: FetchListRequest<ReadProjectRole>) => {
    return ProjectRolesService.getList(projectId, fetchParams);
  };

  const listQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query: listQuery }), [listQuery]);

  return (
    <div className="space-y-2">
      <PageHeader breadcrumbItems={breadcrumbItems} title={title} />
      <ProjectDetailsAndNavigation />
      <QueryTable<ReadProjectRole>
        schema={readProjectRole}
        name={name}
        query={listQuery}
        options={options}
        pathBuilder={(row) =>
          generatePath(PATH_PROJECT_ROLE_EDIT, {
            roleId: row.id,
            projectId,
          })
        }
      >
        <DataTableQueryContextProvider value={queryValue}>
          <ProjectRoleContextProvider>
            <MenubarContainer>
              <QueryTable.Menubar className="mb-0 grow">
                <QueryTable.Menubar.Columns />
                <QueryTable.Menubar.Reset />
              </QueryTable.Menubar>
              <ProjectRoleAddButton projectId={projectId} />
            </MenubarContainer>
            <ProjectRoleListContent />
          </ProjectRoleContextProvider>
        </DataTableQueryContextProvider>
      </QueryTable>
    </div>
  );
};

export default ProjectRoleList;
